/**
 * Laying the seats out as the deck plan draws them, so the holes are visible.
 *
 * <p>A seat list is impossible to check against a deck plan — a missing 14C is one absent line among
 * two hundred. Put the same seats in a grid of rows against seat numbers and the gap is the thing
 * you see first, which is what makes the plan checkable before it is saved.</p>
 */

/** A palette wide enough for the classes a vessel realistically offers, each readable behind white text. */
export const CLASS_COLOURS = [
	'#0f766e',
	'#1d4ed8',
	'#b45309',
	'#7e22ce',
	'#be123c',
	'#15803d',
	'#0369a1',
	'#4d7c0f',
];

/**
 * The colour standing for a class.
 *
 * <p>Its own configured colour when it has one — set on the class, or overridden by the vessel — and
 * otherwise a palette entry chosen by its position in the list. The palette is a fallback so a class
 * nobody has styled still reads as deliberate; it is not the source of truth.</p>
 */
export const classColour = (index: number, configured?: string | null): string =>
	configured && configured.trim().length > 0
		? configured
		: CLASS_COLOURS[index % CLASS_COLOURS.length];

/**
 * The background for one seat.
 *
 * <p>Unclassed seats are neutral — they are sellable to anyone, which is a state rather than an
 * omission. A seat carrying several classes is split evenly between their colours rather than being
 * given a colour of its own, so a two-class seat reads as both and not as a ninth class.</p>
 */
export const seatBackground = (colours: string[]): string => {
	if (colours.length === 0) {
		return '#e5e7eb';
	}
	if (colours.length === 1) {
		return colours[0];
	}

	const share = 100 / colours.length;
	const stops = colours.map((colour, index) => `${colour} ${index * share}% ${(index + 1) * share}%`);
	return `linear-gradient(135deg, ${stops.join(', ')})`;
};

/** The minimum a seat has to carry to be laid out — satisfied by both the admin and wizard shapes. */
export interface GridSeat {
	rowLabel: string;
	seatNumber: string;
	order?: number | null;
	/** Used to put the port side on the left; ignored when the vessel has not been placed. */
	side?: string | null;
	/** The block of the vessel the seat is in. Drives which panel it is drawn in. */
	section?: string | null;
}

/** Where each side sits across the page, so port draws left of centre and centre left of starboard. */
const SIDE_RANK: Record<string, number> = { PORT: 0, CENTRE: 1, STARBOARD: 2 };

/**
 * Labels compared the way a person reads them: `2` before `10`, `B` before `C`.
 *
 * <p>Deliberately not the seats' stored display order. That order is whatever sequence they were
 * generated in, and on a deck with gaps it ranks an axis by which label happens to appear earliest —
 * so a centre column absent from the first row sorted after every outer one, and the layout came out
 * grouped rather than in deck order.</p>
 */
export const naturalCompare = (a: string, b: string): number =>
	a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

export interface SeatGrid<T extends GridSeat> {
	/** Seat numbers, port to starboard and in label order within each side. */
	columns: string[];
	rows: {
		rowLabel: string;
		/** One entry per column; undefined where that row has no such seat. */
		cells: (T | undefined)[];
	}[];
}

/**
 * Groups the seats into rows × seat numbers.
 *
 * <p>Rows read in label order; columns read port, centre, then starboard, and in label order within
 * each. Orientation — which end of the vessel is the bow, and therefore which way up the grid is
 * drawn — is the caller's, because nothing in a seat says where the bow is.</p>
 */
export const buildSeatGrid = <T extends GridSeat>(seats: T[]): SeatGrid<T> => {
	const rowLabels = [...new Set(seats.map(seat => seat.rowLabel))].sort(naturalCompare);

	// A column's side is taken from the first seat in it that carries one — every seat in a column
	// is on the same side of the vessel, and an unplaced vessel simply falls back to label order.
	const sideOfColumn = new Map<string, string | null>();
	seats.forEach(seat => {
		if (seat.side && !sideOfColumn.get(seat.seatNumber)) {
			sideOfColumn.set(seat.seatNumber, seat.side);
		}
	});

	const columnRank = (seatNumber: string) => SIDE_RANK[sideOfColumn.get(seatNumber) ?? ''] ?? 1;

	const columns = [...new Set(seats.map(seat => seat.seatNumber))].sort(
		(a, b) => columnRank(a) - columnRank(b) || naturalCompare(a, b),
	);

	const seatAt = new Map(seats.map(seat => [`${seat.rowLabel}|${seat.seatNumber}`, seat]));

	return {
		columns,
		rows: rowLabels.map(rowLabel => ({
			rowLabel,
			cells: columns.map(seatNumber => seatAt.get(`${rowLabel}|${seatNumber}`)),
		})),
	};
};

/** The side a column of seats is on, for the band of labels drawn above the grid. */
export const columnSides = <T extends GridSeat>(seats: T[], columns: string[]): (string | null)[] => {
	const sides = new Map<string, string | null>();
	seats.forEach(seat => {
		if (seat.side && !sides.get(seat.seatNumber)) {
			sides.set(seat.seatNumber, seat.side);
		}
	});
	return columns.map(column => sides.get(column) ?? null);
};

/** How a side is written on the layout. */
export const SIDE_LABELS: Record<string, string> = {
	PORT: 'Left (port)',
	CENTRE: 'Centre',
	STARBOARD: 'Right (starboard)',
};

/** How wide each divider is drawn by default. A table is the wider because it is a physical object. */
export const AISLE_WIDTH = 10;
export const TABLE_WIDTH = 14;

/** One seat's footprint on the grid, so a divider can be made to match it exactly. */
export const CELL_WIDTH = 40;
export const CELL_HEIGHT = 32;

export interface SeatPanel<T extends GridSeat> {
	key: string;
	/** Empty for a vessel with one unnamed block, so nothing is captioned that needs no caption. */
	title: string;
	seats: T[];
}

/**
 * Splits the seats into the panels the picker draws.
 *
 * <p>By side — port, centre, starboard — and a single panel where the vessel has not been placed.</p>
 *
 * <p>It grouped by section for a while. That put a small grid on the page per block and lost the one
 * thing the layout is for: reading the deck as a single picture. Sections remain on the seat as a
 * label; they no longer decide how it is drawn.</p>
 */
export const buildSeatPanels = <T extends GridSeat>(seats: T[]): SeatPanel<T>[] => {
	// Any side at all, not two or more: a vessel whose seats are all to port should still say so
	// rather than drawing one unnamed block.
	const sides = [...new Set(seats.map(seat => seat.side ?? ''))].filter(Boolean);
	if (sides.length > 0) {
		const order = ['PORT', 'CENTRE', 'STARBOARD'];
		return [
			...sides.sort((a, b) => order.indexOf(a) - order.indexOf(b)),
			...(seats.some(seat => !seat.side) ? [''] : []),
		].map(side => ({
			key: side || 'unplaced',
			title: side ? SIDE_LABELS[side] ?? side : 'Not placed',
			seats: seats.filter(seat => (seat.side ?? '') === side),
		}));
	}

	return seats.length > 0 ? [{ key: 'all', title: '', seats }] : [];
};
